import React, { useState } from 'react';
import { 
  BarChart, Bar, LineChart, Line, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';

// Import sample data
import { seasonalTrendsByCategory, holidaySeasonImpact } from '../../data/timeData';
import { productCategoriesL1 } from '../../data/productData';

const SeasonalTrends = () => {
  const [selectedYear, setSelectedYear] = useState('2023');
  const [selectedChart, setSelectedChart] = useState('radar');
  const [selectedCategories, setSelectedCategories] = useState(
    productCategoriesL1.slice(0, 5).map(cat => cat.name)
  );
  
  // Toggle category selection
  const toggleCategory = (category) => {
    if (selectedCategories.includes(category)) {
      setSelectedCategories(selectedCategories.filter(cat => cat !== category));
    } else {
      setSelectedCategories([...selectedCategories, category]);
    }
  };
  
  // Prepare data for seasonal chart
  const prepareSeasonalData = () => {
    // Reshape data for visualization
    const quarterData = {};
    
    seasonalTrendsByCategory.forEach(quarter => {
      const quarterName = quarter.quarter;
      
      Object.keys(quarter).forEach(key => {
        if (key !== 'quarter') {
          // Add only selected categories
          if (selectedCategories.includes(key)) {
            if (!quarterData[quarterName]) {
              quarterData[quarterName] = { quarter: quarterName };
            }
            
            quarterData[quarterName][key] = quarter[key].index;
          }
        }
      });
    });
    
    return Object.values(quarterData);
  };
  
  // Prepare data for holiday impact chart
  const prepareHolidayImpactData = () => {
    return holidaySeasonImpact
      .filter(item => selectedCategories.includes(item.category))
      .sort((a, b) => b.salesBoost - a.salesBoost);
  };
  
  // Get data for quarter trend chart
  const getQuarterTrendData = () => {
    // Get all growth rates for selected categories across quarters
    const trendData = [];
    
    seasonalTrendsByCategory.forEach(quarter => {
      const entry = { quarter: quarter.quarter };
      
      Object.keys(quarter).forEach(key => {
        if (key !== 'quarter' && selectedCategories.includes(key)) {
          entry[key] = quarter[key].growth;
        }
      });
      
      trendData.push(entry);
    });
    
    return trendData;
  };
  
  // Colors for charts
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#8DD1E1', '#A4DE6C', '#D0ED57'];
  
  // Custom tooltip for radar chart
  const renderRadarTooltip = (props) => {
    const { active, payload } = props;
    
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 shadow-sm rounded-md">
          <p className="font-semibold">{payload[0].payload.quarter}</p>
          {payload.map((entry, index) => (
            <p className="text-sm" key={index} style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    
    return null;
  };
  
  // Render radar chart
  const renderRadarChart = () => {
    return (
      <ResponsiveContainer width="100%" height={500}>
        <RadarChart data={prepareSeasonalData()} cx="50%" cy="50%">
          <PolarGrid />
          <PolarAngleAxis dataKey="quarter" />
          <PolarRadiusAxis angle={30} domain={[70, 160]} />
          {selectedCategories.map((category, index) => (
            <Radar 
              key={category}
              name={category}
              dataKey={category}
              stroke={COLORS[index % COLORS.length]}
              fill={COLORS[index % COLORS.length]}
              fillOpacity={0.5}
            />
          ))}
          <Tooltip content={renderRadarTooltip} />
          <Legend />
        </RadarChart>
      </ResponsiveContainer>
    );
  };
  
  // Render line chart
  const renderLineChart = () => {
    return (
      <ResponsiveContainer width="100%" height={500}>
        <LineChart
          data={getQuarterTrendData()}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="quarter" />
          <YAxis />
          <Tooltip />
          <Legend />
          {selectedCategories.map((category, index) => (
            <Line 
              key={category}
              type="monotone"
              dataKey={category}
              stroke={COLORS[index % COLORS.length]}
              activeDot={{ r: 8 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    );
  };
  
  return (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">Seasonal Trends Analysis</h1>
        <p className="text-gray-600 mb-4">
          Analyze seasonal patterns and holiday impact across different product categories. Identify peak seasons and optimize inventory planning.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-3">Analysis Year</label>
            <select 
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="dropdown-selector"
            >
              <option value="2023">2023</option>
              <option value="2024">2024</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-3">Chart Type</label>
            <div className="flex">
              <button
                onClick={() => setSelectedChart('radar')}
                className={`px-3 py-2 text-sm font-medium rounded-l-md ${
                  selectedChart === 'radar' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Radar Chart
              </button>
              <button
                onClick={() => setSelectedChart('line')}
                className={`px-3 py-2 text-sm font-medium rounded-r-md ${
                  selectedChart === 'line' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Line Chart
              </button>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-3">Product Categories</label>
            <div className="flex flex-wrap gap-2">
              {productCategoriesL1.slice(0, 6).map(category => (
                <button
                  key={category.id}
                  onClick={() => toggleCategory(category.name)}
                  className={`px-3 py-1 text-xs font-medium rounded-full ${
                    selectedCategories.includes(category.name)
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Seasonal Index Visualization */}
      <div className="border border-gray-200 rounded-lg p-4 mb-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-3">
          {selectedChart === 'radar' ? 'Seasonal Index by Quarter' : 'Quarterly Growth Trends'}
        </h2>
        <div className="mb-2 text-sm text-gray-600">
          {selectedChart === 'radar' 
            ? 'Index values: 100 = average quarter, higher values indicate stronger seasonal performance' 
            : 'Growth percentage compared to previous quarter'}
        </div>
        
        {selectedChart === 'radar' ? renderRadarChart() : renderLineChart()}
      </div>
      
      {/* Holiday Season Impact */}
      <div className="border border-gray-200 rounded-lg p-4 mb-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-3">Holiday Season Impact</h2>
        <p className="mb-3 text-sm text-gray-600">
          Sales boost percentage during holiday season for selected categories
        </p>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={prepareHolidayImpactData()}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="category" />
              <YAxis />
              <Tooltip formatter={(value) => `${value}%`} />
              <Legend />
              <Bar dataKey="salesBoost" fill="#0088FE" name="Sales Boost %" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* Data Table */}
      <div>
        <h2 className="text-lg font-semibold text-gray-800 mb-3">Seasonal Patterns Detail</h2>
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Q1 Index</th>
                <th>Q2 Index</th>
                <th>Q3 Index</th>
                <th>Q4 Index</th>
                <th>Holiday Boost</th>
                <th>Peak Week</th>
              </tr>
            </thead>
            <tbody>
              {productCategoriesL1
                .filter(cat => selectedCategories.includes(cat.name))
                .map(category => {
                  const q1Data = seasonalTrendsByCategory.find(q => q.quarter === 'Q1-2023')?.[category.name] || {};
                  const q2Data = seasonalTrendsByCategory.find(q => q.quarter === 'Q2-2023')?.[category.name] || {};
                  const q3Data = seasonalTrendsByCategory.find(q => q.quarter === 'Q3-2023')?.[category.name] || {};
                  const q4Data = seasonalTrendsByCategory.find(q => q.quarter === 'Q4-2023')?.[category.name] || {};
                  const holidayData = holidaySeasonImpact.find(h => h.category === category.name) || {};
                  
                  return (
                    <tr key={category.id}>
                      <td>{category.name}</td>
                      <td>{q1Data.index || '-'}</td>
                      <td>{q2Data.index || '-'}</td>
                      <td>{q3Data.index || '-'}</td>
                      <td>{q4Data.index || '-'}</td>
                      <td>{holidayData.salesBoost ? `${holidayData.salesBoost}%` : '-'}</td>
                      <td>{holidayData.peakWeek || '-'}</td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SeasonalTrends;